import React, { useEffect, useState, useRef, forwardRef, FC } from 'react';
import classnames from 'classnames';
import Link from 'next/link';
import BScroll from '@better-scroll/core';

import styles from './YouMayLike.module.scss';
import useCart from '@/store/shopping';
import { WeeeIcon } from '@/components/common/Icon';
import useTranslation from 'next-translate/useTranslation';
import { buildUrlWithOrigin } from '@/utils/tool';
import { getCroppedImageUrl } from '@/components/common/CroppedImage';
import { twMerge } from 'tailwind-merge';

export interface Props {
  type: 'horizontal' | 'vertical';
  listRef?: React.MutableRefObject<HTMLDivElement>;
  handleVisChange?: (ymlRef: number) => void;
  className?: string;
  style?: React.CSSProperties;
}

const AlsoLike: FC<Props> = ({ type, listRef, handleVisChange, className, style }) => {
  const { ymlInfo = [], handleSyncYouMayLike } = useCart();
  const { t, lang } = useTranslation('common');
  const [visible, setVisible] = useState<boolean>(false);
  const [halfVisble, setHalfVisble] = useState<boolean>(false);
  const ymlRef = useRef<HTMLDivElement>();
  const ymlListRef = useRef<HTMLDivElement>();
  const scrollRef = useRef<HTMLDivElement>();
  const scrollRefObj = useRef<any>(null);
  const scrollYRef = useRef<number>(0);
  const isSafari = typeof window !== 'undefined' && navigator.userAgent.indexOf('Safari') !== -1;
  const isIphone = typeof window !== 'undefined' && navigator.userAgent.indexOf('iPhone') !== -1;
  const isMobileIosSafari = isSafari && isIphone;

  /**数据变化时展示 */
  useEffect(() => {
    if (ymlInfo.length > 0) {
      setVisible(true);
      setHalfVisble(false);
    } else handleClose();
  }, [ymlInfo]);

  /**初始化左右滚动 */
  useEffect(() => {
    scrollRefObj.current = new BScroll(scrollRef.current, {
      scrollX: true,
      probeType: 3,
      eventPassthrough: type === 'vertical' ? '' : 'vertical',
      useTransition: false,
      click: true
    });
  }, [type]);

  /**重置滚动区域 */
  useEffect(() => {
    if (scrollRefObj.current) {
      scrollRefObj.current.refresh();
      scrollRefObj.current.scrollTo(0, 0, 0);
    }
  }, [ymlInfo, visible]);

  /**滑动交互 */
  useEffect(() => {
    if (visible) {
      if (type === 'vertical') {
        // 滑动列表收起
        listRef && listRef.current.addEventListener('touchmove', handleTouchMove, true);
      } else {
        // 横版滑动到可视区域
        ymlRef.current.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
      }
    } else {
      if (type === 'vertical') {
        listRef && listRef.current.removeEventListener('touchmove', handleTouchMove, true);
      }
    }
  }, [visible]);

  /**动态修改progressBar高度 */
  useEffect(() => {
    let height = 0;
    if (visible) height = 114;
    if (visible && halfVisble) height = 48;
    handleVisChange && handleVisChange(height);
  }, [visible, halfVisble]);

  /**滑动穿透（safari特有问题，目前只有fixed解决方案） */
  useEffect(() => {
    if (type === 'horizontal') return;
    if (isMobileIosSafari) {
      if (visible && !halfVisble) {
        scrollYRef.current = window.pageYOffset;
        document.body.style.position = 'fixed';
        document.body.style.top = `-${scrollYRef.current}px`;
        document.body.style.width = '100%';
      } else {
        document.body.style.removeProperty('position');
        document.body.style.removeProperty('top');
        document.body.style.removeProperty('width');
        window.scrollTo(0, scrollYRef.current);
      }
    }
    return () => {
      document.body.style.removeProperty('position');
      document.body.style.removeProperty('top');
      document.body.style.removeProperty('width');
    };
  }, [visible, halfVisble, type]);

  const handleTouchMove = (e: any) => {
    if (!halfVisble) setHalfVisble(true);
  };

  const handleClose = () => {
    setVisible(false);
    setHalfVisble(false);
  };

  // 设置样式类
  const classNames = twMerge(
    'w-full mt-2 box-border rounded-md opacity-0',
    classnames({
      [styles[`also-like-${type}`]]: type,
      [styles[`also-like-${type}-visible`]]: visible,
      [styles[`also-like-${type}-halfvisible`]]: visible && halfVisble
    })
  );

  return (
    <div className={classnames(classNames, className)} style={style} ref={ymlRef}>
      <div
        className="flex justify-between items-center p-4"
        onClick={() => {
          setHalfVisble(half => !half);
        }}>
        <p className="enki-body-2xs">{t('also_like')}</p>
        <div
          onClick={() => {
            handleSyncYouMayLike([], null);
            handleClose();
          }}>
          <WeeeIcon type="iconTimes" style={{ fontSize: 22 }} />
        </div>
      </div>
      <div className="whitespace-nowrap w-full overflow-hidden text-surface-200-fg-default" ref={scrollRef}>
        <div className="inline-block mb-2 px-3" ref={ymlListRef}>
          <div style={{ minWidth: '1px' }}></div>
          {ymlInfo.map(item => {
            return (
              <Link
                prefetch={false}
                key={item.id}
                href={buildUrlWithOrigin(`/${lang}/product/${item?.slug ? item?.slug : `weee/${item?.id}`}`)}>
                <div
                  key={item.id}
                  style={{ width: 88, minWidth: 88, height: 122 }}
                  className="inline-block rounded-lg mr-2 whitespace-normal align-middle bg-surface-100-bg">
                  <img
                    className="w-full rounded-t-400"
                    height={72}
                    src={getCroppedImageUrl(item.img, { renderHeight: 72, devicePixelRatio: 2 })}
                    alt="product image"
                  />
                  <div className="flex items-center h-10">
                    <p className="line-clamp-2 px-2 enki-body-2xs">{item.name}</p>
                  </div>
                </div>
              </Link>
            );
          })}
          <div style={{ minWidth: '1px' }}></div>
        </div>
      </div>
    </div>
  );
};

export default AlsoLike;
